import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService, ApplicationSettings, HeaderService, SpinnerService, UserService } from 'dspx-core';
import { Observable } from 'rxjs';
import * as fileSaver from 'file-saver';
import { take } from 'rxjs/operators';
import { APPLICATION } from "../constants/app-data.constants";
import { ExportToDataTablesRequest } from '../interfaces/export-to-data-tables-request.interface';
import { ExportToDataTablesResponse } from '../interfaces/export-to-data-tables-response.interface';
import { Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class AppDataService extends ApiService {

  appData: { [key: string]: any } = {};

  headerConfig: any;

  constructor(protected httpClient: HttpClient,
    private appSettings: ApplicationSettings,
    private spinnerService: SpinnerService,
    private userService: UserService,
    private headerService: HeaderService,
    private titleService: Title,
  ) {
    super(httpClient, appSettings.apiUrl + 'api');
  }

  public addToApiBaseUrlsNotRequiringSpinner(apiBaseUrls: string[]) {
    this.appSettings.apiBaseUrlsNotRequiringSpinner.push(...apiBaseUrls);
  }

  public setAppData(key: string, value: any) {
    this.appData[key] = value;
  }

  public getAppData(key: string): any {
    return this.appData[key];
  }

  public generatePdf(relativeUrl: string, downloadFile = true) {
    const url = this.getUrl(relativeUrl);

    this.httpClient
      .post(url, null, { responseType: 'blob', observe: 'response' })
      .subscribe(
        response => {
          const dispositionHeader = response.headers.get('content-disposition');
          if (!dispositionHeader) {
            throw new Error('content-disposition header is not found');
          }

          //if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          //  //IE and Edge :  presents option to save or open
          //  window.navigator.msSaveOrOpenBlob(response.body);
          //}
          //else {
          //All modern browsers such as Chrome
          if (downloadFile === true) {
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.download = dispositionHeader.split(';')[1].trim().split('=')[1];
            a.href = URL.createObjectURL(response.body);
            a.target = '_blank';
            a.click();
            document.body.removeChild(a);
          } else {
            const fileURL = URL.createObjectURL(response.body);
            window.open(fileURL, 'PdfTabWindow');
          }
          //}
        },
        err => {
          throw err;
        }
      );
  }

  private getContentFileName(contentDispositionHeader: string): string {
    const parts: string[] = contentDispositionHeader.split(';');
    const fileName = parts[1].split('=')[1];
    return fileName.replace(/['"]+/g, '');
  }

  public delete(endpoint: string): Observable<Object> {
    return super.delete(endpoint);
  }

  public post<T>(endpoint: string, request: T): Observable<any> {
    return this.postAndFetch<T>(endpoint, request);
  }

  public fetchData(endpoint: string): Observable<any> {
    return this.postAndFetch<any>(endpoint, {});
  }

  public getData<T>(endpoint: string, requestBody: any): Observable<any> {
    return this.get<T>(endpoint, requestBody);
  }

  public getCsv(relativeUrl: string, requestBody: any, defaultFileName: string) {
    const url = this.getUrl(relativeUrl);

    this.spinnerService.showSpinner();
    this.httpClient
      .post(url, requestBody, { responseType: 'blob', observe: 'response' })
      .subscribe(
        response => {
          this.spinnerService.hideSpinner();
          let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          const data: Blob = new Blob([response.body], {
            type: EXCEL_TYPE
          });
          fileSaver.saveAs(data, defaultFileName + '_' + new Date().getTime() + '.csv');
        },
        err => {
          throw err;
        }
      );
  }

  public getDataTableSet(
    apiEndpoint: string,
    request: ExportToDataTablesRequest,
    onSuccess: (response: ExportToDataTablesResponse, transientId: string) => void): void {
    this.post(apiEndpoint, request)
      .pipe(take(1))
      .subscribe((response: ExportToDataTablesResponse) => onSuccess(response, this.getTransientId()));
  }

  public getTransientId(): string {
    return `${new Date().getTime()}`;
  }

  public getAttributeFromHtmlElement(elementId: string, attributeName: string): string {
    return document.getElementById(elementId)?.attributes[attributeName]?.value;
  }

  public getValueAttributeFromHtmlElement(elementId: string): string {
    return this.getAttributeFromHtmlElement(elementId, 'value');
  }

  public getUsersInitials(name: string) {
    // Split name property by space delimiter
    const wordsArray = (name && name.split(' ')) || '';
    let initials = '';
    const firstInitial = wordsArray[0].charAt(0);
    let lastInitial = '';
    // If more than one word in name property
    if (wordsArray.length > 1) {
      // If the last character is not a number or special character we can use it
      if (/^[a-zA-Z ,_]*$/.test(wordsArray[wordsArray.length - 1].charAt(0)) === true) {
        lastInitial = wordsArray[wordsArray.length - 1].charAt(0);
        initials = firstInitial.toUpperCase() + lastInitial.toUpperCase();
      } else { // Else only use the inital from the first name.
        initials = firstInitial.toUpperCase();
      }
    } else {
      initials = firstInitial.toUpperCase();
    }
    return initials;
  }

  public getUserDetails() {
    // Get Logged In User and update header
    this.userService
      .getLoggedInUser('getLoggedInUser')
      .pipe(take(1))
      .subscribe((userProfile) => {
        this.headerConfig = {
          applicationTitle: {
            label: APPLICATION.title,
          },
          applicationLogo: {
            source: APPLICATION.logo,
          },
          loggedInUser: {
            name: userProfile.fullName,
            imageSource: '/assets/images/avatar.png',
            imageDescription: userProfile.fullName,
            menuSubItems: [
              {
                label: 'Logout',
                icon: 'pi pi-fw pi-sign-out',
                routerLink: 'logout',
              },
            ],
          },
        };
        this.headerService.onHeaderUpdated(this.headerConfig);
      });
  }

  // The DSPX framework sets the HTML title based on the application title, the application sub-title, the form name, the current navigation tab, and the current page.
  // In this application we need to set the HTML title based only on the application title and the form name, so we override the value set by the DSPX framework.
  public overrideHtmlTitleSetByDspxFramework(formName: string): void {
    this.titleService.setTitle(`${APPLICATION.title} - ${formName}`);
  }
}
